import { useState, useEffect, memo } from 'react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup, Annotation } from 'react-simple-maps';

// Belgian provinces TopoJSON (detailed boundaries)
const BELGIUM_GEO_URL = "/belgium-provinces-topo.json";

// Province name translations and center coordinates for labels
// Using NAME_2 values from the TopoJSON file
const PROVINCE_INFO = {
  "Antwerpen": { fr: "Anvers", center: [4.4, 51.22] },
  "Limburg": { fr: "Limbourg", center: [5.4, 50.95] },
  "Oost-Vlaanderen": { fr: "Flandre-Orientale", center: [3.75, 51.0] },
  "West-Vlaanderen": { fr: "Flandre-Occidentale", center: [3.0, 51.05] },
  "Vlaams Brabant": { fr: "Brabant flamand", center: [4.6, 50.88] },
  "Bruxelles": { fr: "Bruxelles", center: [4.35, 50.85] },
  "Brabant Wallon": { fr: "Brabant wallon", center: [4.5, 50.6] },
  "Hainaut": { fr: "Hainaut", center: [3.9, 50.45] },
  "Namur": { fr: "Namur", center: [4.85, 50.25] },
  "Liège": { fr: "Liège", center: [5.55, 50.5] },
  "Luxembourg": { fr: "Luxembourg", center: [5.5, 49.85] }
};

// Major Belgian cities with coordinates and alternative names
const BELGIAN_CITIES = [
  { name: "Bruxelles", altNames: ["Brussels", "Brussel"], coordinates: [4.3517, 50.8503], province: "Brussels" },
  { name: "Antwerpen", altNames: ["Antwerp", "Anvers"], coordinates: [4.4025, 51.2194], province: "Antwerpen" },
  { name: "Gent", altNames: ["Ghent", "Gand"], coordinates: [3.7174, 51.0543], province: "Oost-Vlaanderen" },
  { name: "Charleroi", altNames: [], coordinates: [4.4444, 50.4108], province: "Hainaut" },
  { name: "Liège", altNames: ["Luik"], coordinates: [5.5697, 50.6326], province: "Liège" },
  { name: "Brugge", altNames: ["Bruges"], coordinates: [3.2247, 51.2093], province: "West-Vlaanderen" },
  { name: "Namur", altNames: ["Namen"], coordinates: [4.8621, 50.4669], province: "Namur" },
  { name: "Leuven", altNames: ["Louvain"], coordinates: [4.7005, 50.8798], province: "Vlaams-Brabant" },
  { name: "Mons", altNames: ["Bergen"], coordinates: [3.9515, 50.4542], province: "Hainaut" },
  { name: "Mechelen", altNames: ["Malines"], coordinates: [4.4803, 51.0259], province: "Antwerpen" },
  { name: "Aalst", altNames: ["Alost"], coordinates: [4.0355, 50.9378], province: "Oost-Vlaanderen" },
  { name: "Hasselt", altNames: [], coordinates: [5.3378, 50.9307], province: "Limburg" },
  { name: "Kortrijk", altNames: ["Courtrai"], coordinates: [3.2646, 50.8279], province: "West-Vlaanderen" },
  { name: "Tournai", altNames: ["Doornik"], coordinates: [3.3883, 50.6058], province: "Hainaut" },
  { name: "Arlon", altNames: ["Aarlen"], coordinates: [5.8176, 49.6833], province: "Luxembourg" },
  { name: "Genk", altNames: [], coordinates: [5.5003, 50.9654], province: "Limburg" },
  { name: "Ostende", altNames: ["Oostende", "Ostend"], coordinates: [2.9286, 51.2154], province: "West-Vlaanderen" },
  { name: "La Louvière", altNames: [], coordinates: [4.1856, 50.4792], province: "Hainaut" },
  { name: "Seraing", altNames: [], coordinates: [5.5, 50.6167], province: "Liège" },
  { name: "Verviers", altNames: [], coordinates: [5.8667, 50.5833], province: "Liège" },
  { name: "Mouscron", altNames: ["Moeskroen"], coordinates: [3.2167, 50.7333], province: "Hainaut" },
];

// Region colors - matching the legend exactly
const REGION_COLORS = {
  // Flanders - light blue (as in legend)
  "Vlaanderen": "#B3E0FF",
  // Brussels - light pink (as in legend) 
  "Bruxelles": "#FFCCD5",
  // Wallonia - light green (as in legend)
  "Wallonie": "#C8E6C9",
};

// Get color based on region (NAME_1 property from TopoJSON)
const getRegionColor = (properties) => {
  // Get the region from NAME_1 (Bruxelles, Vlaanderen, or Wallonie)
  const region = properties?.NAME_1;
  return REGION_COLORS[region] || "#B3E0FF"; // Default to Flanders blue
};

const BelgiumMap = memo(function BelgiumMap({ 
  data = {}, 
  mode = 'users', // 'users', 'careers', 'domains'
  onProvinceClick = () => {},
  language = 'fr'
}) {
  const [hoveredProvince, setHoveredProvince] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, content: '' });
  const [geoLoaded, setGeoLoaded] = useState(false);

  // Calculate users per province based on city data
  const getUsersPerProvince = (provinceName) => {
    // Map cities to provinces (approximate)
    const cityToProvince = {
      "Bruxelles": ["Brussels Hoofdstedelijk Gewest", "Bruxelles-Capitale"],
      "Brussels": ["Brussels Hoofdstedelijk Gewest", "Bruxelles-Capitale"],
      "Brussel": ["Brussels Hoofdstedelijk Gewest", "Bruxelles-Capitale"],
      "Antwerpen": ["Antwerpen"],
      "Anvers": ["Antwerpen"],
      "Gent": ["Oost-Vlaanderen"],
      "Gand": ["Oost-Vlaanderen"],
      "Ghent": ["Oost-Vlaanderen"],
      "Liège": ["Liège"],
      "Luik": ["Liège"],
      "Charleroi": ["Hainaut"],
      "Mons": ["Hainaut"],
      "Bergen": ["Hainaut"],
      "Namur": ["Namur"],
      "Namen": ["Namur"],
      "Hasselt": ["Limburg"],
      "Leuven": ["Vlaams-Brabant"],
      "Louvain": ["Vlaams-Brabant"],
      "Bruges": ["West-Vlaanderen"],
      "Brugge": ["West-Vlaanderen"],
      "Arlon": ["Luxembourg"],
      "Aarlen": ["Luxembourg"],
      "Wavre": ["Brabant wallon"],
      "Waver": ["Brabant wallon"],
    };

    let count = 0;
    const citiesData = { ...data.by_city, ...data.by_municipality };
    
    for (const [city, cityCount] of Object.entries(citiesData)) {
      const provinces = cityToProvince[city] || [];
      if (provinces.includes(provinceName)) {
        count += cityCount;
      }
    }
    
    return count;
  };

  // Get user count for a city (check all name variants)
  const getCityData = (city) => {
    const allNames = [city.name, ...city.altNames];
    let totalCount = 0;
    
    for (const name of allNames) {
      // Check exact match
      totalCount += data.by_city?.[name] || 0;
      totalCount += data.by_municipality?.[name] || 0;
      
      // Check case-insensitive
      const lowerName = name.toLowerCase();
      for (const [key, value] of Object.entries(data.by_city || {})) {
        if (key.toLowerCase() === lowerName && key !== name) {
          totalCount += value;
        }
      }
      for (const [key, value] of Object.entries(data.by_municipality || {})) {
        if (key.toLowerCase() === lowerName && key !== name) {
          totalCount += value;
        }
      }
    }
    
    return totalCount;
  };

  // Get marker size based on user count
  const getCitySize = (count) => {
    if (count === 0) return 0;
    if (count < 3) return 5;
    if (count < 5) return 7;
    if (count < 10) return 9;
    if (count < 25) return 11;
    return 14;
  };

  // Get province data based on mode
  const getProvinceInfo = (provinceName) => {
    const provinceInfo = PROVINCE_INFO[provinceName];
    const displayName = language === 'fr' && provinceInfo ? provinceInfo.fr : provinceName;
    const userCount = getUsersPerProvince(provinceName);
    
    if (mode === 'careers') {
      const career = data.careers_by_region?.[provinceName];
      return (
        <div>
          <div className="font-bold">{displayName}</div>
          <div className="text-xs opacity-80">{userCount} utilisateur{userCount > 1 ? 's' : ''}</div>
          {career && <div className="mt-1">Métier: {career.career} ({career.count})</div>}
        </div>
      );
    }
    if (mode === 'domains') {
      const domain = data.domains_by_region?.[provinceName];
      return (
        <div>
          <div className="font-bold">{displayName}</div>
          <div className="text-xs opacity-80">{userCount} utilisateur{userCount > 1 ? 's' : ''}</div>
          {domain && <div className="mt-1">Domaine: {domain.domain} ({domain.count})</div>}
        </div>
      );
    }
    // Users mode
    return (
      <div>
        <div className="font-bold">{displayName}</div>
        <div className="text-orange-300 font-semibold">{userCount} utilisateur{userCount > 1 ? 's' : ''}</div>
      </div>
    );
  };

  const handleMouseMove = (evt, geo) => {
    // Use NAME_2 for province name
    const provinceName = geo.properties?.NAME_2 || geo.properties?.NAME_1 || 'Unknown';
    setTooltip({
      show: true,
      x: evt.clientX,
      y: evt.clientY,
      content: getProvinceInfo(provinceName)
    });
    setHoveredProvince(provinceName);
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, x: 0, y: 0, content: '' });
    setHoveredProvince(null);
  };

  return (
    <div className="relative w-full h-full min-h-[400px]">
      {/* Tooltip */}
      {tooltip.show && (
        <div 
          className="fixed z-50 bg-[#1D1D1D] text-white px-3 py-2 rounded-lg text-sm shadow-lg pointer-events-none"
          style={{ 
            left: tooltip.x + 10, 
            top: tooltip.y + 10,
            transform: 'translateX(-50%)'
          }}
        >
          {tooltip.content}
        </div>
      )}

      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          center: [4.5, 50.5],
          scale: 8000
        }}
        style={{ width: "100%", height: "100%" }}
      >
        <ZoomableGroup center={[4.5, 50.5]} zoom={1}>
          <Geographies geography={BELGIUM_GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                // Use NAME_2 for province name (Antwerpen, Liège, etc.) or NAME_1 for region
                const provinceName = geo.properties?.NAME_2 || geo.properties?.NAME_1 || 'Unknown';
                const isHovered = hoveredProvince === provinceName;
                // Get color based on region (NAME_1: Bruxelles, Vlaanderen, Wallonie)
                const baseColor = getRegionColor(geo.properties);
                
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseMove={(evt) => handleMouseMove(evt, geo)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => onProvinceClick(provinceName)}
                    style={{
                      default: {
                        fill: baseColor,
                        fillOpacity: 0.9,
                        stroke: "#666666",
                        strokeWidth: 1.5,
                        outline: "none",
                        cursor: "pointer"
                      },
                      hover: {
                        fill: baseColor,
                        fillOpacity: 1,
                        stroke: "#333333",
                        strokeWidth: 2.5,
                        outline: "none",
                        cursor: "pointer"
                      },
                      pressed: {
                        fill: baseColor,
                        fillOpacity: 1,
                        stroke: "#333333",
                        strokeWidth: 2.5,
                        outline: "none"
                      }
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* City markers */}
          {BELGIAN_CITIES.map((city) => {
            const count = getCityData(city);
            const size = getCitySize(count);
            if (size === 0) return null;
            
            return (
              <Marker key={city.name} coordinates={city.coordinates}>
                <circle
                  r={size}
                  fill="#FB6823"
                  fillOpacity={0.85}
                  stroke="#FFFFFF"
                  strokeWidth={2}
                  className="cursor-pointer transition-all hover:fill-opacity-100"
                  style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                />
                {size >= 7 && (
                  <text
                    textAnchor="middle"
                    y={size + 14}
                    style={{ 
                      fontFamily: "system-ui", 
                      fill: "#1D1D1D",
                      fontSize: "10px",
                      fontWeight: "600",
                      textShadow: "0 0 3px white, 0 0 3px white"
                    }}
                  >
                    {city.name}
                  </text>
                )}
                <title>{`${city.name}: ${count} utilisateurs`}</title>
              </Marker>
            );
          })}
          
          {/* Province labels */}
          {Object.entries(PROVINCE_INFO).map(([name, info]) => (
            <Marker key={`label-${name}`} coordinates={info.center}>
              <text
                textAnchor="middle"
                style={{
                  fontFamily: "system-ui",
                  fill: "#374151",
                  fontSize: "8px",
                  fontWeight: "500",
                  textShadow: "0 0 2px white, 0 0 2px white, 0 0 2px white",
                  pointerEvents: "none"
                }}
              >
                {language === 'fr' ? info.fr : name}
              </text>
            </Marker>
          ))}
        </ZoomableGroup>
      </ComposableMap>

      {/* Legend */}
      <div className="absolute bottom-2 left-2 bg-white/95 backdrop-blur rounded-lg p-3 text-xs shadow-md">
        <div className="font-semibold text-[#1D1D1D] mb-2">Légende</div>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#FB6823]" />
            <span>Ville avec utilisateurs</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded bg-[#B3E0FF]" />
            <span>Flandre</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded bg-[#C8E6C9]" />
            <span>Wallonie</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded bg-[#FFCCD5]" />
            <span>Bruxelles</span>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-gray-200 text-[10px] text-gray-500">
          Survolez une province pour voir les détails
        </div>
      </div>
    </div>
  );
});

export default BelgiumMap;
